import { Box, chakra, Flex, HStack, Stack, Table, Text } from '@chakra-ui/react';
import { ArrowRight, ExternalLink, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { Quake } from '../api/types';
import { defensiveAdmit, devWarnLeakedMovers } from '../lib/defensive';
import { pctFromProb, pp, spreadPct } from '../lib/format';
import { polymarketEventUrlFromEventSlug } from '../lib/polymarket';
import { eventDisplayTitle, eventThesis, marketDisplayLabel } from './eventThesis';
import { ScoreBreakdownCells } from './ScoreBreakdown';
import { ScoreGauge } from './ScoreGauge';

interface EventGroupsTableProps {
  movers: Quake[];
  /** Top watchlist-band movers, used as "almost confirmed" content
   *  when the user's current mode is empty. */
  watchlistFallback: Quake[];
  isLoading: boolean;
  mode: 'confirmed' | 'watchlist';
  rowCount: number;
  selectedMoverKey?: string | undefined;
  onSelectMover?: ((mover: Quake) => void) | undefined;
  onSwitchMode?: ((mode: 'confirmed' | 'watchlist') => void) | undefined;
  onViewAllMovers?: (() => void) | undefined;
}

interface EventGroup {
  eventSlug: string;
  eventTitle: string;
  movers: Quake[];
  heat: number;
  maxScore: number;
  breadthBonus: number;
  agreementBonus: number;
  marketsMoving: number;
  agreementPct: number;
  dominantDirection: 'up' | 'down' | 'flat';
  dominantDirectionLabel: string;
  topMover: Quake;
  thesis: string;
}

// EventGroupsTable groups live quakes by event (EventSlug). The user's
// mental model is event-first ("Belgium vs Egypt is moving"), not
// market-first. The table shows one row per event with a thesis
// sentence, a heat score (max + breadth + agreement), the dominant
// direction, and a per-event rollup. Expanding a row drills into the
// individual markets under that event.
//
// The "Strict feed" subtitle below the title tells the user that
// absence is intentional, not broken — the engine rejects candidates
// that fail any gate before they become a row.
export function EventGroupsTable({
  movers,
  watchlistFallback,
  isLoading,
  mode,
  rowCount,
  selectedMoverKey,
  onSelectMover,
  onSwitchMode,
  onViewAllMovers,
}: EventGroupsTableProps) {
  // Backend owns admission. The client does not reapply detector gates.
  const { admitted, leaked } = useMemo(() => defensiveAdmit(movers), [movers]);
  devWarnLeakedMovers(leaked);

  const groups = useMemo(() => groupMoversByEvent(admitted), [admitted]);
  const visibleGroups = groups.slice(0, rowCount);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const title = mode === 'watchlist' ? 'Watchlist Event Moves' : 'Confirmed Event Moves';

  return (
    <Box as="section" layerStyle="glassPanel" borderRadius="oqPanel" overflow="hidden">
      <Flex align="center" justify="space-between" borderBottomWidth="1px" borderColor="border.subtle" px={5} py={3}>
        <HStack gap={3}>
          <Box display="grid" placeItems="center" h={6} w={6} borderRadius="oqControl" bg="brand.solid/80" fontSize="sm" fontWeight="bold" color="brand.fg">
            1
          </Box>
          <Text fontSize="lg" fontWeight="semibold" color="text.primary">
            {title}
          </Text>
          <Text fontSize="xs" color="text.muted">
            by event
          </Text>
        </HStack>
        {isLoading ? <Loader2 className="oq-spin" size={16} color="currentColor" /> : null}
      </Flex>

      <Box borderBottomWidth="1px" borderColor="border.subtle" bg="surface.control" px={5} py={2} fontSize="xs" color="text.muted">
        Strict feed: one heat model blends move, volume, capital, tail persistence, liquidity quality, and baseline confidence.
        Absence means heat did not clear the selected mode threshold.
      </Box>

      <Box className="scrollbar-soft" overflowX="auto">
        <Table.Root size="sm" variant="line" stickyHeader minW="1200px">
          <Table.Header bg="surface.panel">
            <Table.Row borderBottomWidth="1px" borderColor="border.subtle" textAlign="left" fontSize="xs" fontWeight="medium" color="text.muted">
              <Table.ColumnHeader w={10} px={3} py={3} />
              <Table.ColumnHeader px={3} py={3}>
                Event
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Heat
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Markets
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Agreement
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Direction
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Top Move
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={3}>
                Score breakdown
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleGroups.map((group) => {
              const isOpen = expanded.has(group.eventSlug);
              return (
                <EventGroupRows
                  key={group.eventSlug}
                  group={group}
                  isOpen={isOpen}
                  selectedMoverKey={selectedMoverKey}
                  onToggle={() => {
                    setExpanded((prev) => {
                      const next = new Set(prev);
                      if (next.has(group.eventSlug)) {
                        next.delete(group.eventSlug);
                      } else {
                        next.add(group.eventSlug);
                      }
                      return next;
                    });
                  }}
                  onSelectMover={onSelectMover}
                />
              );
            })}
            {visibleGroups.length === 0 && !isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={8} px={5} py={6} textAlign="center" fontSize="sm" color="text.muted">
                  {mode === 'confirmed' && watchlistFallback.length > 0 ? (
                    <AlmostConfirmed
                      movers={watchlistFallback}
                      onSwitchToWatchlist={() => onSwitchMode?.('watchlist')}
                      onSelectMover={onSelectMover}
                    />
                  ) : (
                    <Stack gap={1}>
                      <Text>
                        No movers in this mode yet. The scanner is running; the heat threshold is strict on purpose.
                      </Text>
                      {mode === 'confirmed' && onSwitchMode ? (
                        <chakra.button
                          type="button"
                          onClick={() => onSwitchMode('watchlist')}
                          fontSize="xs"
                          color="signal.blue"
                          textDecoration="underline"
                          _hover={{ color: 'text.primary' }}
                        >
                          Switch to Watchlist to see near-misses
                        </chakra.button>
                      ) : null}
                    </Stack>
                  )}
                </Table.Cell>
              </Table.Row>
            ) : null}
          </Table.Body>
        </Table.Root>
      </Box>

      {onViewAllMovers ? (
        <chakra.button
          type="button"
          onClick={onViewAllMovers}
          display="flex"
          w="full"
          alignItems="center"
          justifyContent="center"
          gap={2}
          borderTopWidth="1px"
          borderColor="border.subtle"
          py={3}
          fontSize="sm"
          fontWeight="medium"
          color="text.muted"
          transition="colors"
          _hover={{ color: 'text.primary' }}
        >
          View all movers <ExternalLink size={14} />
        </chakra.button>
      ) : null}
    </Box>
  );
}

function EventGroupRows({
  group,
  isOpen,
  selectedMoverKey,
  onToggle,
  onSelectMover,
}: {
  group: EventGroup;
  isOpen: boolean;
  selectedMoverKey?: string | undefined;
  onToggle: () => void;
  onSelectMover?: ((mover: Quake) => void) | undefined;
}) {
  const url = polymarketEventUrlFromEventSlug(group.eventSlug);
  return (
    <>
      <Table.Row
        cursor="pointer"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        _hover={{ bg: 'surface.control.hovered' }}
        transition="backgrounds"
        onClick={onToggle}
      >
        <Table.Cell px={3} py={2.5} color="text.muted">
          {isOpen ? '▾' : '▸'}
        </Table.Cell>
        <Table.Cell px={3} py={2.5}>
          <Flex align="flex-start" gap={2}>
            <Box minW={0}>
              <Text fontWeight="medium" color="text.primary" truncate title={group.eventSlug || group.eventTitle}>
                {group.eventTitle}
              </Text>
              <Text mt={1} fontSize="xs" color="text.muted">
                {group.thesis}
              </Text>
            </Box>
            {url ? (
              <chakra.a
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                aria-label="Open event on Polymarket"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                mt={0.5}
                flexShrink={0}
                color="text.muted"
                transition="colors"
                _hover={{ color: 'signal.blue' }}
              >
                <ExternalLink size={16} />
              </chakra.a>
            ) : null}
          </Flex>
        </Table.Cell>
        <Table.Cell px={3} py={2.5}>
          <ScoreGauge score={group.heat} mover={group.topMover} small />
        </Table.Cell>
        <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
          {group.marketsMoving}
        </Table.Cell>
        <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
          {group.agreementPct.toFixed(0)}%
        </Table.Cell>
        <Table.Cell whiteSpace="nowrap" px={3} py={2.5}>
          <DominantDirectionBadge direction={group.dominantDirection} label={group.dominantDirectionLabel} />
        </Table.Cell>
        <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
          {pp(group.topMover.change_pp)}
        </Table.Cell>
        <Table.Cell px={3} py={2.5}>
          <ScoreBreakdownCells mover={group.topMover} />
        </Table.Cell>
      </Table.Row>
      {isOpen ? (
        <Table.Row>
          <Table.Cell colSpan={8} borderBottomWidth="1px" borderColor="border.subtle" bg="blackAlpha.200" p={0}>
            <EventMarketsPanel
              group={group}
              selectedMoverKey={selectedMoverKey}
              onSelectMover={onSelectMover}
            />
          </Table.Cell>
        </Table.Row>
      ) : null}
    </>
  );
}

function EventMarketsPanel({
  group,
  selectedMoverKey,
  onSelectMover,
}: {
  group: EventGroup;
  selectedMoverKey?: string | undefined;
  onSelectMover?: ((mover: Quake) => void) | undefined;
}) {
  return (
    <Box px={4} py={3}>
      <Box className="scrollbar-soft" overflowX="auto" borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="surface.control">
        <Table.Root size="sm" variant="line" minW="980px">
          <Table.Header>
            <Table.Row borderBottomWidth="1px" borderColor="border.subtle" textAlign="left" fontSize="11px" fontWeight="semibold" textTransform="uppercase" letterSpacing="wide" color="text.muted">
              <Table.ColumnHeader px={3} py={2.5}>
                Market
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Outcome
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Price
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Move
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Spike
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Persist
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Spread
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Score
              </Table.ColumnHeader>
              <Table.ColumnHeader px={3} py={2.5}>
                Drivers
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {group.movers.map((mover) => {
              const positive = mover.change_pp >= 0;
              const selected = selectedMoverKey === (mover.market_id || mover.asset_id);
              return (
                <Table.Row
                  key={mover.asset_id}
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  bg={selected ? 'brand.subtle' : undefined}
                  _hover={{ bg: 'surface.control.hovered' }}
                  transition="backgrounds"
                  _last={{ borderBottomWidth: 0 }}
                >
                  <Table.Cell maxW="360px" px={3} py={2.5}>
                    <chakra.button
                      type="button"
                      onClick={() => onSelectMover?.(mover)}
                      className="oq-clamp-2"
                      textAlign="left"
                      fontWeight="medium"
                      lineHeight="snug"
                      color="text.primary"
                      transition="colors"
                      _hover={{ color: 'signal.blue' }}
                      title={mover.question}
                    >
                      {marketDisplayLabel(mover.question, group.eventTitle)}
                    </chakra.button>
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5}>
                    <OutcomeBadge label={mover.outcome || (positive ? 'YES' : 'NO')} positive={positive} />
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
                    {pctFromProb(mover.old_price)}{' '}
                    <Box as="span" display="inline-block" mx={1} verticalAlign="baseline" w="3" h="3" color="text.muted">
                      <ArrowRight />
                    </Box>{' '}
                    {pctFromProb(mover.new_price)}
                  </Table.Cell>
                  <Table.Cell
                    whiteSpace="nowrap"
                    px={3}
                    py={2.5}
                    fontWeight="semibold"
                    color={positive ? 'signal.good' : 'signal.bad'}
                  >
                    {pp(mover.change_pp)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.primary">
                    {ratio(mover.volume_spike_ratio)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} fontWeight="semibold" color="signal.good">
                    {ratio(mover.directional_persistence)}
                  </Table.Cell>
                  <Table.Cell whiteSpace="nowrap" px={3} py={2.5} color="text.muted">
                    {spreadPct(mover.spread)}
                  </Table.Cell>
                  <Table.Cell px={3} py={2.5}>
                    <ScoreGauge score={mover.score} mover={mover} small />
                  </Table.Cell>
                  <Table.Cell px={3} py={2.5}>
                    <ScoreBreakdownCells mover={mover} />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>
    </Box>
  );
}

function OutcomeBadge({ label, positive }: { label: string; positive: boolean }) {
  return (
    <Box
      as="span"
      title={label}
      display="inline-block"
      maxW="8rem"
      truncate
      whiteSpace="nowrap"
      borderRadius="oqControl"
      px={2}
      py={0.5}
      verticalAlign="middle"
      fontSize="11px"
      fontWeight="black"
      textTransform="uppercase"
      letterSpacing="wide"
      bg={positive ? 'signal.good/12' : 'signal.bad/12'}
      color={positive ? 'signal.good' : 'signal.bad'}
    >
      {label}
    </Box>
  );
}

function DominantDirectionBadge({
  direction,
  label,
}: {
  direction: 'up' | 'down' | 'flat';
  label: string;
}) {
  if (direction === 'up') {
    return (
      <Stack
        as="span"
        direction="row"
        align="center"
        gap={1}
        display="inline-flex"
        borderRadius="oqControl"
        bg="signal.good/10"
        px={2}
        py={1}
        fontSize="xs"
        fontWeight="semibold"
        color="signal.good"
      >
        <span aria-hidden>▲</span>
        {label}
      </Stack>
    );
  }
  if (direction === 'down') {
    return (
      <Stack
        as="span"
        direction="row"
        align="center"
        gap={1}
        display="inline-flex"
        borderRadius="oqControl"
        bg="signal.bad/10"
        px={2}
        py={1}
        fontSize="xs"
        fontWeight="semibold"
        color="signal.bad"
      >
        <span aria-hidden>▼</span>
        {label}
      </Stack>
    );
  }
  return (
    <Stack
      as="span"
      direction="row"
      align="center"
      gap={1}
      display="inline-flex"
      borderRadius="oqControl"
      borderWidth="1px"
      borderColor="border.subtle"
      bg="surface.control.hovered"
      px={2}
      py={1}
      fontSize="xs"
      fontWeight="semibold"
      color="text.muted"
    >
      {label}
    </Stack>
  );
}

// groupMoversByEvent folds movers into per-event groups and computes
// the rollups the table renders.
//
// Heat is NOT the mean score. The mean punishes broad correlated
// events with one weak sibling and rewards one-market events with a
// single high score. The dashboard's mental model is event-level, so
// the headline should reward both strength AND breadth while capping
// thin one-market moves:
//
//   heat = maxScore + min(marketsMoving - 1, 4) * 3 + agreementBonus
//
// where agreementBonus is +5 if dominant direction covers 100% of
// movers, +2 if >= 75%, and 0 otherwise. One-market events cap at 70
// unless the move is >= 5pp or the capital-weighted move is extreme.
function groupMoversByEvent(admitted: readonly Quake[]): EventGroup[] {
  const byEvent = new Map<string, Quake[]>();
  for (const mover of admitted) {
    const key = mover.event_slug || mover.market_id || mover.asset_id;
    const bucket = byEvent.get(key);
    if (bucket) {
      bucket.push(mover);
    } else {
      byEvent.set(key, [mover]);
    }
  }
  const groups: EventGroup[] = [];
  for (const [eventSlug, movers] of byEvent) {
    if (movers.length === 0) continue;
    movers.sort((a, b) => b.score - a.score);
    const topMover = movers[0];
    if (!topMover) continue;
    const ups = movers.filter((m) => m.direction === 'up').length;
    const downs = movers.filter((m) => m.direction === 'down').length;
    const dominantDirection: 'up' | 'down' | 'flat' = ups > downs ? 'up' : downs > ups ? 'down' : 'flat';
    const dominantCount = dominantDirection === 'up' ? ups : dominantDirection === 'down' ? downs : 0;
    const agreementPct = movers.length === 0 ? 0 : (dominantCount / movers.length) * 100;

    const maxScore = movers.reduce((max, m) => Math.max(max, m.score), 0);
    const breadthBonus = Math.min(movers.length - 1, 4) * 3;
    const agreementBonus = agreementPct >= 100 ? 5 : agreementPct >= 75 ? 2 : 0;
    const heat = eventHeat(topMover, movers.length, maxScore + breadthBonus + agreementBonus);

    const dominantDirectionLabel =
      dominantDirection === 'up' ? 'Up' : dominantDirection === 'down' ? 'Down' : 'Mixed';

    const eventTitle = eventDisplayTitle(eventSlug, movers);
    const thesis = eventThesis({
      eventSlug,
      eventTitle,
      movers,
      dominantDirection,
      agreementPct,
    });

    groups.push({
      eventSlug,
      eventTitle,
      movers,
      heat,
      maxScore,
      breadthBonus,
      agreementBonus,
      marketsMoving: movers.length,
      agreementPct,
      dominantDirection,
      dominantDirectionLabel,
      topMover,
      thesis,
    });
  }
  groups.sort((a, b) => b.heat - a.heat);
  return groups;
}

function eventHeat(topMover: Quake, marketsMoving: number, rawHeat: number): number {
  let heat = Math.max(0, Math.min(100, rawHeat));
  const extremeMove = Math.abs(topMover.change_pp) >= 5;
  const extremeCapital =
    topMover.dynamic_min_capital_weighted_move > 0 &&
    topMover.capital_weighted_move >= topMover.dynamic_min_capital_weighted_move * 8;

  if (marketsMoving === 1 && !extremeMove && !extremeCapital) {
    heat = Math.min(heat, 70);
  } else if (marketsMoving === 2) {
    heat = Math.min(heat, 85);
  }

  return heat;
}

function ratio(value: number) {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(value >= 10 ? 0 : 2)}x`;
}

// AlmostConfirmed renders the top watchlist-band movers when the user's
// current mode is empty. This is the "lead time" thesis — even when no
// event has cleared the strict 65+ heat threshold, the user sees the
// highest-scoring near-misses with a one-click path to switch modes.
function AlmostConfirmed({
  movers,
  onSwitchToWatchlist,
  onSelectMover,
}: {
  movers: readonly Quake[];
  onSwitchToWatchlist: () => void;
  onSelectMover?: ((mover: Quake) => void) | undefined;
}) {
  const top = movers.slice(0, 5);
  if (top.length === 0) return null;
  return (
    <Stack gap={3} align="stretch" py={2}>
      <Stack gap={1}>
        <Text fontSize="sm" fontWeight="semibold" color="text.primary">
          Almost confirmed — heat 60-64, watching
        </Text>
        <Text fontSize="xs" color="text.muted">
          No event cleared 65+ in this window. The top {top.length} near-miss{top.length === 1 ? '' : 'es'} below are closest to confirmation.
        </Text>
      </Stack>
      <Stack gap={2} align="stretch">
        {top.map((mover) => {
          const selected = (mover.market_id || mover.asset_id) !== '';
          return (
            <Flex
              key={mover.asset_id}
              align="center"
              justify="space-between"
              gap={3}
              borderRadius="oqControl"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="blackAlpha.100"
              px={3}
              py={2}
              _hover={{ borderColor: 'signal.blue/30' }}
            >
              <Box minW={0} flex={1}>
                <chakra.button
                  type="button"
                  onClick={() => onSelectMover?.(mover)}
                  textAlign="left"
                  fontSize="sm"
                  fontWeight="medium"
                  color="text.primary"
                  _hover={{ color: 'signal.blue' }}
                  className="oq-clamp-1"
                  title={mover.question}
                >
                  {mover.question}
                </chakra.button>
                <Text mt={0.5} fontSize="xs" color="text.muted">
                  {mover.category} · {pp(mover.change_pp)} · reliability {(mover.reliability * 100).toFixed(0)}%
                </Text>
              </Box>
              <Flex align="center" gap={3}>
                <Text fontSize="sm" fontWeight="semibold" color="signal.warn">
                  heat {mover.score.toFixed(0)}
                </Text>
                <ScoreGauge score={mover.score} mover={mover} small />
                {selected ? null : null}
              </Flex>
            </Flex>
          );
        })}
      </Stack>
      <chakra.button
        type="button"
        onClick={onSwitchToWatchlist}
        alignSelf="center"
        display="inline-flex"
        alignItems="center"
        gap={2}
        borderRadius="oqControl"
        borderWidth="1px"
        borderColor="signal.blue/30"
        bg="signal.blue/10"
        px={4}
        py={2}
        fontSize="sm"
        fontWeight="semibold"
        color="text.primary"
        transition="all 0.2s"
        _hover={{ bg: 'signal.blue/15', color: 'text.primary' }}
      >
        Open Watchlist feed
        <ArrowRight size={14} />
      </chakra.button>
    </Stack>
  );
}